import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle, Users, Target, CheckCircle, XCircle,
  Clock, MessageSquare, RefreshCw, ClipboardCheck, ArrowRight,
  HelpCircle, ChevronDown,
} from 'lucide-react';
import { programFramework } from '../data/programFramework';
import { PROGRAM_FAQS } from '../data/programFaqs';
import MedicalReviewByline from './MedicalReviewByline';
import RelatedResources from './RelatedResources';

const Block = ({ icon: Icon, title, accent, children, testid }) => (
  <div
    className={`rounded-2xl border ${accent} bg-slate-900/50 p-6 sm:p-7`}
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-slate-800/80 flex items-center justify-center text-blue-400 flex-shrink-0">
        <Icon className="w-5 h-5" aria-hidden="true" />
      </div>
      <h3 className="text-lg sm:text-xl font-semibold text-white">{title}</h3>
    </div>
    {children}
  </div>
);

const List = ({ items, icon: Icon = CheckCircle, color = 'text-emerald-400' }) => (
  <ul className="space-y-2.5">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2.5 text-slate-300 text-sm sm:text-base leading-relaxed">
        <Icon className={`w-4 h-4 ${color} flex-shrink-0 mt-1`} aria-hidden="true" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

const ProgramFramework = ({ slug }) => {
  const p = programFramework[slug];
  const faqs = PROGRAM_FAQS[slug] || [];
  const [openFaq, setOpenFaq] = useState(null);
  if (!p) return null;

  return (
    <section className="py-16 sm:py-20 bg-slate-950 border-t border-slate-800" data-testid="program-framework">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
              How This Program <span className="text-blue-400">Works</span>
            </h2>
            <MedicalReviewByline className="mx-auto" />
          </div>

          <div className="grid gap-5 sm:gap-6">
            <Block icon={AlertCircle} title="The problem it solves" accent="border-orange-500/20" testid="pf-problem">
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">{p.problem}</p>
            </Block>

            <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
              <Block icon={Users} title="Who it's designed for" accent="border-blue-500/20" testid="pf-who">
                <List items={p.who} icon={CheckCircle} color="text-blue-400" />
              </Block>
              <Block icon={Target} title="Expected outcomes" accent="border-emerald-500/20" testid="pf-outcomes">
                <List items={p.outcomes} />
              </Block>
            </div>

            <Block icon={ClipboardCheck} title="What's included & what's not" accent="border-slate-700" testid="pf-scope">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-emerald-400 font-semibold text-sm mb-2">Inclusions</p>
                  <List items={p.scope.inclusions} />
                </div>
                <div>
                  <p className="text-rose-400 font-semibold text-sm mb-2">Exclusions</p>
                  <List items={p.scope.exclusions} icon={XCircle} color="text-rose-400" />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-800">
                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-white text-sm font-semibold">Support frequency</p>
                    <p className="text-slate-400 text-sm">{p.scope.frequency}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <MessageSquare className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-white text-sm font-semibold">Channels</p>
                    <p className="text-slate-400 text-sm">{p.scope.channels.join(' · ')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <RefreshCw className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-white text-sm font-semibold">Review process</p>
                    <p className="text-slate-400 text-sm">{p.scope.review}</p>
                  </div>
                </div>
              </div>
            </Block>

            <Block icon={CheckCircle} title="Your responsibilities" accent="border-blue-500/20" testid="pf-responsibilities">
              <List items={p.responsibilities} icon={CheckCircle} color="text-blue-400" />
            </Block>
          </div>

          {/* Standardized FAQ */}
          {faqs.length > 0 && (
            <div className="mt-12" data-testid="pf-faqs">
              <div className="flex items-center justify-center gap-2.5 mb-6">
                <HelpCircle className="w-5 h-5 text-blue-400" aria-hidden="true" />
                <h3 className="text-2xl sm:text-3xl font-bold text-white">Common Questions</h3>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                {faqs.map((f, i) => (
                  <div key={i} className={`bg-slate-900/50 rounded-2xl border ${openFaq === i ? 'border-blue-500/40' : 'border-slate-800 hover:border-slate-700'} transition-colors`}>
                    <button
                      onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      aria-expanded={openFaq === i}
                      className="w-full flex items-center justify-between gap-3 p-5 text-left"
                      data-testid={`pf-faq-toggle-${i}`}
                    >
                      <span className="text-white font-semibold text-sm sm:text-base pr-4">{f.q}</span>
                      <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180 text-blue-400' : 'text-slate-500'}`} aria-hidden="true" />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-[500px]' : 'max-h-0'}`}>
                      <p className="px-5 pb-5 text-slate-400 text-sm sm:text-base leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to={`/book-consultation?from=program-${slug}`}
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-7 py-3.5 rounded-full font-bold transition-all shadow-lg shadow-blue-500/25"
              data-testid="pf-cta"
            >
              Book a Free Consultation <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            <Link
              to="/how-we-work"
              className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white text-sm underline underline-offset-4 decoration-slate-600 hover:decoration-white transition-colors"
              data-testid="pf-methodology-link"
            >
              See our full coaching methodology <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>

          <RelatedResources slug={slug} className="mt-12" />
        </div>
      </div>
    </section>
  );
};

export default ProgramFramework;
